import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Eye, Users, Globe, Link2 } from 'lucide-react';

const PERIODS = [
  { id: 1, label: '1g' }, { id: 7, label: '7g' }, { id: 30, label: '30g' }, { id: 90, label: '90g' },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.adminAnalyticsSummary(days)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const max = Math.max(1, ...(data?.daily?.map(d => d.views) || [1]));

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Trafik Analitiği</div>
          <h1 className="font-display text-4xl mt-2 leading-none">Ziyaretçi & Sayfa Görüntülenmesi</h1>
        </div>
        <div className="inline-flex items-center p-1 bg-white border border-ink-100 rounded-full">
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => setDays(p.id)} className={`px-4 h-9 rounded-full text-xs font-semibold tabular tracking-widest transition-colors ${days === p.id ? 'bg-ink-600 text-cream' : 'text-ink-500 hover:text-ink-600'}`}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading && <div className="bg-white rounded-2xl p-8 text-center text-ink-400 border border-ink-100">Yükleniyor...</div>}

      {!loading && data && (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <Card icon={Eye} label="Toplam Görüntülenme" value={(data.total_views || 0).toLocaleString('tr-TR')} tone="coral" />
            <Card icon={Users} label="Tekil Ziyaretçi" value={(data.unique_sessions || 0).toLocaleString('tr-TR')} tone="gold" />
            <Card icon={Globe} label="Ülke Sayısı" value={data.countries?.length || 0} tone="emerald" />
          </div>

          {/* Time series chart */}
          <div className="bg-white rounded-2xl border border-ink-100 p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Günlük</div>
                <h2 className="font-display text-xl mt-1">Sayfa Görüntülenmeleri</h2>
              </div>
            </div>
            {data.daily.length === 0 && <div className="text-ink-400 text-sm py-8 text-center">Bu aralıkta veri yok.</div>}
            {data.daily.length > 0 && (
              <div className="flex items-end gap-1 h-48 relative">
                {data.daily.map(d => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.views} görüntülenme`}>
                    <div className="text-[10px] text-ink-400 opacity-0 group-hover:opacity-100 tabular">{d.views}</div>
                    <div className="w-full bg-gradient-to-t from-gold to-gold-400 rounded-t transition-all hover:from-coral-300" style={{ height: `${(d.views / max) * 100}%`, minHeight: '4px' }} />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 flex justify-between text-[10px] text-ink-400 tabular">
              {data.daily.length > 0 && (
                <>
                  <span>{data.daily[0]?.date}</span>
                  <span>{data.daily[data.daily.length - 1]?.date}</span>
                </>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <ListBlock icon={Link2} title="En Çok Görüntülenen Sayfalar" items={data.top_pages || []} keyField="path" prefixSlash />
            <ListBlock icon={Globe} title="Ülkeler" items={data.countries || []} keyField="country" />
            <ListBlock icon={Link2} title="Trafik Kaynakları" items={data.referrers || []} keyField="referrer" wide />
          </div>
        </>
      )}
    </div>
  );
}

function Card({ icon: Icon, label, value, tone }) {
  const t = { coral: 'bg-coral-50 text-coral-300', gold: 'bg-gold/15 text-gold-500', emerald: 'bg-emerald-50 text-emerald-600' }[tone];
  return (
    <div className="bg-white rounded-2xl p-5 border border-ink-100">
      <div className={`w-11 h-11 rounded-xl grid place-items-center ${t}`}><Icon className="w-5 h-5" /></div>
      <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">{label}</div>
      <div className="mt-1.5 font-display text-3xl text-ink-600 tabular leading-none">{value}</div>
    </div>
  );
}

function ListBlock({ icon: Icon, title, items, keyField, prefixSlash, wide }) {
  const max = Math.max(1, ...items.map(x => x.views));
  return (
    <div className={`bg-white rounded-2xl border border-ink-100 p-6 ${wide ? 'lg:col-span-2' : ''}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-coral-300" />
        <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">{title}</div>
      </div>
      {items.length === 0 && <div className="text-ink-400 text-sm py-6 text-center">Veri yok.</div>}
      <div className="space-y-2.5">
        {items.map((it, i) => {
          const v = it[keyField];
          const display = prefixSlash ? v : (keyField === 'referrer' ? new URL(v.startsWith('http') ? v : 'https://' + v).hostname : v);
          return (
            <div key={i} className="relative">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="text-sm text-ink-600 truncate flex-1" title={v}>{display}</div>
                <div className="text-xs font-semibold tabular text-ink-500">{it.views}</div>
              </div>
              <div className="h-1.5 bg-ink-50 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-gold to-coral-300 rounded-full" style={{ width: `${(it.views / max) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
